'use client'

import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilLeftArrowFromLeft as ToggleSidebarIcon } from '@tooni/iconscout-unicons-react'

import { type MouseEvent, type ReactNode, useEffect, useState } from 'react'

import isBoolean from 'lodash/fp/isBoolean'
import isEmpty from 'lodash/fp/isEmpty'
import omit from 'lodash/fp/omit'

import type { SidebarEntries } from '../../types'

import { usePathname } from '../../helpers/translations'
import useScrollInfo from '../../hooks/useScrollInfo'
import useTranslations from '../../hooks/useTranslations'
import SidebarEntry from './SidebarEntry'
import SidebarGroup from './SidebarGroup'

type Props = {
  button?: ReactNode
  className?: string
  collapsed?: boolean
  collapseOn?: Array<string>
  color?: 'primary' | 'secondary'
  entries?: SidebarEntries
  fullWidth?: boolean
  onClick?: () => void
}
export default function Sidebar({
  button,
  className,
  collapsed = false,
  collapseOn = [],
  color = 'secondary',
  entries = [],
  fullWidth = false,
  onClick,
}: Props) {
  const pathname = usePathname()
  const $t = useTranslations()

  const [scrollInfo, scrollableSidebar] = useScrollInfo()
  const scrollHeight = scrollInfo.y.total ?? 0
  const currentScroll = scrollInfo.y.value ?? 0

  const maskBottom =
    scrollHeight > 0 &&
    (currentScroll === 0 || scrollHeight - currentScroll >= 5 * 8)

  const [isCollapsed, setIsCollapsed] = useState(!fullWidth && collapsed)

  useEffect(() => {
    if (isEmpty(collapseOn)) {
      return
    }

    setIsCollapsed(
      collapseOn.some((path) => {
        if (path.startsWith('!')) {
          return !pathname.startsWith(path.slice(1))
        }

        return pathname.startsWith(path)
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseOn.length, pathname])

  const toggleCollapse = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    setIsCollapsed((isCurrentlyCollapsed) => !isCurrentlyCollapsed)
  }

  return (
    <nav
      className={cn(
        'box-border h-full flex-col items-start justify-between gap-3 p-3',
        color === 'primary'
          ? 'bg-primary text-dark'
          : 'text-grey-30 bg-secondary',
        fullWidth
          ? 'w-full'
          : 'hidden lg:flex transition-[width,min-width] duration-600',
        fullWidth
          ? ''
          : isCollapsed
            ? 'w-9 min-w-9'
            : 'w-[240px] min-w-[240px]',
        className,
      )}
    >
      <div
        ref={scrollableSidebar}
        className={cj(
          'no-scrollbar flex size-full flex-col overflow-y-scroll',
          fullWidth ? 'gap-8' : 'gap-6',
          maskBottom ? 'mask-bottom-5' : '',
        )}
      >
        {entries
          .filter(
            (group) =>
              !isEmpty(group) &&
              group?.find(
                (entry) =>
                  entry === true ||
                  (!isBoolean(entry) && entry?.position !== 'bottom'),
              ),
          )
          .map((group, groupIndex) => (
            <SidebarGroup key={groupIndex}>
              {group
                ?.filter(
                  (entry) =>
                    entry === true ||
                    (!isBoolean(entry) && entry?.position !== 'bottom'),
                )
                .map((entry, entryIndex) =>
                  isBoolean(entry) ? (
                    entry && (
                      <hr
                        key={`${groupIndex}-separator-${entryIndex}`}
                        className={cj(
                          'border-b-1 w-full my-0',
                          color === 'primary'
                            ? 'border-normal'
                            : 'border-grey-30',
                        )}
                      />
                    )
                  ) : (
                    <SidebarEntry
                      key={`${groupIndex}-entry-${entryIndex}`}
                      {...omit(['subEntries'], entry)}
                      collapsed={!fullWidth && isCollapsed}
                      color={color}
                      expandSubEntries={entry?.expanded}
                      fullWidth={fullWidth}
                      onClick={onClick}
                    >
                      {(fullWidth || !isCollapsed) &&
                        !isEmpty(entry?.subEntries) &&
                        entry?.subEntries
                          ?.filter(Boolean)
                          .map((subEntry, subEntryIndex) =>
                            isBoolean(subEntry) ? (
                              subEntry && (
                                <hr
                                  key={`${groupIndex}-${entryIndex}-separator-${subEntryIndex}`}
                                  className={cj(
                                    'border-b-1 w-full ml-5 my-0',
                                    color === 'primary'
                                      ? 'border-normal'
                                      : 'border-grey-30',
                                  )}
                                />
                              )
                            ) : (
                              <SidebarEntry
                                key={`${groupIndex}-${entryIndex}-entry-${subEntryIndex}`}
                                color={color}
                                fullWidth={fullWidth}
                                onClick={onClick}
                                {...subEntry}
                              />
                            ),
                          )}
                    </SidebarEntry>
                  ),
                )}
            </SidebarGroup>
          ))}

        <div className="flex justify-center p-1">{button}</div>

        <div className="h-full" />

        {entries
          .filter(
            (group) =>
              !isEmpty(group) &&
              group?.find(
                (entry) => !isBoolean(entry) && entry?.position === 'bottom',
              ),
          )
          .map((group, groupIndex) => (
            <SidebarGroup key={groupIndex}>
              {group
                ?.filter(
                  (entry) => !isBoolean(entry) && entry?.position === 'bottom',
                )
                .map((entry, entryIndex) =>
                  isBoolean(entry) ? (
                    entry && (
                      <hr
                        key={`${groupIndex}-separator-${entryIndex}`}
                        className={cj(
                          'border-b-1 w-full my-0',
                          color === 'primary'
                            ? 'border-normal'
                            : 'border-grey-30',
                        )}
                      />
                    )
                  ) : (
                    <SidebarEntry
                      key={`${groupIndex}-entry-${entryIndex}`}
                      {...omit(['subEntries'], entry)}
                      collapsed={!fullWidth && isCollapsed}
                      color={color}
                      expandSubEntries={entry?.expanded}
                      fullWidth={fullWidth}
                      onClick={onClick}
                    >
                      {(fullWidth || !isCollapsed) &&
                        !isEmpty(entry?.subEntries) &&
                        entry?.subEntries
                          ?.filter(Boolean)
                          .map((subEntry, subEntryIndex) =>
                            isBoolean(subEntry) ? (
                              subEntry && (
                                <hr
                                  key={`${groupIndex}-${entryIndex}-separator-${subEntryIndex}`}
                                  className={cj(
                                    'border-b-1 w-full ml-5 my-0',
                                    color === 'primary'
                                      ? 'border-normal'
                                      : 'border-grey-30',
                                  )}
                                />
                              )
                            ) : (
                              <SidebarEntry
                                key={`${groupIndex}-${entryIndex}-entry-${subEntryIndex}`}
                                color={color}
                                fullWidth={fullWidth}
                                onClick={onClick}
                                {...subEntry}
                              />
                            ),
                          )}
                    </SidebarEntry>
                  ),
                )}
            </SidebarGroup>
          ))}
      </div>

      {!fullWidth && (
        <Button
          className={cj(
            'transition-transform duration-600',
            color === 'primary' ? 'text-dark' : 'text-grey-30 hover:text-white',
            isCollapsed ? 'rotate-180 transform' : 'self-start',
          )}
          icon={<ToggleSidebarIcon />}
          iconOnly
          label={isCollapsed ? $t('general.expand') : $t('general.collapse')}
          variant="text"
          onClick={(event) =>
            toggleCollapse(event as MouseEvent<HTMLButtonElement>)
          }
        />
      )}
    </nav>
  )
}
